import React, { useState } from "react";
import { ClipboardList, MapPin } from "lucide-react";

import { Button } from "components/ui/Button";
import { Notice, NoticeText } from "components/ui/Notice";
import { Select } from "components/ui/Select/Select";
import { TabSwitcher } from "components/ui/TabSwitcher";

import { COUNTY_OPTIONS, PRACTICE_TYPE_OPTIONS } from "../../constants";
import { getFieldError } from "../../getFieldError";
import { AddressAutocomplete } from "../AddressAutocomplete/AddressAutocomplete";
import { PracticeField } from "../PracticeField/PracticeField";
import { PracticeLocationMap } from "../PracticeLocationMap/PracticeLocationMap";
import { PracticeTextField } from "../PracticeTextField/PracticeTextField";
import styles from "./PracticeDetailsForm.module.css";

const SMS_NAME_MAX_LENGTH = 11;

const DETAILS_TAB = "details";
const ADDRESS_TAB = "address";

const TAB_OPTIONS = [
  { value: DETAILS_TAB, label: "Details", icon: <ClipboardList size={16} /> },
  { value: ADDRESS_TAB, label: "Address", icon: <MapPin size={16} /> },
];

/*
 * Which fields sit on which tab, and how each one is drawn. The panels are rendered
 * from these lists and the tab a failed submit jumps to is derived from them too, so a
 * field cannot be moved between tabs and then forgotten by the error handling.
 *
 * Details holds who the practice is and how to reach it; Address holds where it is,
 * with the map that pins it underneath.
 */
const DETAILS_FIELDS = [
  {
    name: "name",
    label: "Practice Name",
    placeholder: "Practice Name",
    isRequired: true,
    span: "wide",
  },
  {
    name: "type",
    kind: "select",
    label: "Practice Type",
    options: PRACTICE_TYPE_OPTIONS,
    isRequired: true,
    span: "quarter",
  },
  {
    name: "website",
    label: "Practice Website",
    placeholder: "Website",
    span: "third",
  },
  {
    name: "mainPhone",
    label: "Practice phone",
    placeholder: "Phone",
    isRequired: true,
    span: "quarter",
  },
  {
    name: "practiceEmail",
    label: "Practice email",
    placeholder: "Email",
    isRequired: true,
    span: "wide",
  },
  {
    name: "smsName",
    label: "Sms name",
    placeholder: "Sms Name",
    maxLength: SMS_NAME_MAX_LENGTH,
    span: "third",
  },
  { name: "registeredPracticeId", label: "CPS Id", placeholder: "CPS Id" },
  {
    name: "twitter",
    label: "External Social Links",
    placeholder: "twitter",
  },
];

// The three lines share one required "Practice Address" label, as they always have.
const ADDRESS_LINE_FIELDS = [
  { name: "address", placeholder: "First line", span: "third" },
  { name: "address2", placeholder: "Second line", span: "third" },
  { name: "address3", placeholder: "Third line", span: "third" },
];

const ADDRESS_AREA_FIELDS = [
  {
    name: "county",
    kind: "select",
    label: "County",
    options: COUNTY_OPTIONS,
    isRequired: true,
    span: "quarter",
  },
  {
    name: "city",
    label: "City",
    placeholder: "City",
    isRequired: true,
    span: "quarter",
  },
  {
    name: "country",
    label: "Country",
    placeholder: "Country",
    isRequired: true,
    span: "quarter",
  },
  {
    name: "eircode",
    label: "Eircode",
    placeholder: "Eircode",
    isRequired: true,
    span: "quarter",
  },
];

const FIELD_TABS = [
  ...DETAILS_FIELDS.map(({ name }) => [name, DETAILS_TAB]),
  ...ADDRESS_LINE_FIELDS.map(({ name }) => [name, ADDRESS_TAB]),
  ...ADDRESS_AREA_FIELDS.map(({ name }) => [name, ADDRESS_TAB]),
];

const ConfiguredField = ({ formik, field, onSelectChange }) => {
  const errorMessage = getFieldError(formik, field.name);

  if (field.kind === "select") {
    return (
      <PracticeField
        label={field.label}
        isRequired={field.isRequired}
        errorMessage={errorMessage}
        span={field.span}
      >
        <Select
          name={field.name}
          options={field.options}
          value={formik.values[field.name]}
          onChange={onSelectChange(field.name)}
          error={!!errorMessage}
          ariaRequired={field.isRequired}
          fullWidth
        />
      </PracticeField>
    );
  }

  return (
    <PracticeTextField
      formik={formik}
      name={field.name}
      label={field.label}
      placeholder={field.placeholder}
      isRequired={field.isRequired}
      span={field.span}
      maxLength={field.maxLength}
      ariaLabel={field.ariaLabel}
    />
  );
};

export const PracticeDetailsForm = ({
  formik,
  isSaving,
  autocomplete,
  mapsScript,
  fullAddress,
  onEircodeChange,
  onLocationChange,
}) => {
  const [activeTab, setActiveTab] = useState(DETAILS_TAB);

  /*
   * Formik marks a select touched on blur, which the shared Select does not expose,
   * so the choice itself is what makes its error eligible to show.
   */
  const selectField = (name) => (value) => {
    formik.setFieldValue(name, value);
    formik.setFieldTouched(name, true);
  };

  /*
   * Enter is swallowed for the whole form: the address autocomplete lives inside it
   * and picking a prediction with Enter must not submit the practice.
   */
  const preventSubmitOnEnter = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  /*
   * Required fields live on both tabs, so a rejected submit could otherwise render its
   * error on a tab the user cannot see — Save would appear to do nothing at all. The
   * tab holding the first invalid field is brought forward before Formik submits.
   */
  const submitWithFirstErrorInView = async (event) => {
    event.preventDefault();

    const errors = await formik.validateForm();
    const firstInvalidField = FIELD_TABS.find(([name]) => !!errors[name]);

    if (firstInvalidField) setActiveTab(firstInvalidField[1]);

    formik.submitForm();
  };

  return (
    <form
      name="form"
      autoComplete="off"
      className={styles.form}
      onSubmit={submitWithFirstErrorInView}
      onKeyDown={preventSubmitOnEnter}
    >
      <TabSwitcher
        options={TAB_OPTIONS}
        value={activeTab}
        onChange={setActiveTab}
        fullWidth
      />

      {activeTab === DETAILS_TAB && (
        <div className={styles.grid}>
          {DETAILS_FIELDS.map((field) => (
            <ConfiguredField
              key={field.name}
              formik={formik}
              field={field}
              onSelectChange={selectField}
            />
          ))}
        </div>
      )}

      {activeTab === ADDRESS_TAB && (
        <div className={styles.grid}>
          <PracticeField
            label="Search Address"
            htmlFor="addressSearch"
            span="full"
          >
            <AddressAutocomplete
              inputId="addressSearch"
              value={autocomplete.addressSearch}
              results={autocomplete.results}
              isSearching={autocomplete.isSearching}
              containerRef={autocomplete.containerRef}
              inputRef={autocomplete.inputRef}
              onChange={autocomplete.handleSearchChange}
              onKeyDown={autocomplete.handleSearchKeyDown}
              onSelect={autocomplete.selectAddress}
              onLeaveResults={autocomplete.focusSearchInput}
            />

            {autocomplete.isUnavailable ? (
              <Notice tone="info" testId="address-search-unavailable">
                <NoticeText>
                  Address suggestions are unavailable at the moment. The address
                  fields below can be filled in manually, and they are what gets
                  saved.
                </NoticeText>
              </Notice>
            ) : (
              <small className={styles.helpText}>
                Start typing to search for addresses, or fill in the fields
                manually below
              </small>
            )}
          </PracticeField>

          <PracticeField label="Practice Address" isRequired span="full">
            <div className={styles.addressLines}>
              {ADDRESS_LINE_FIELDS.map((field) => (
                <ConfiguredField
                  key={field.name}
                  formik={formik}
                  field={{ ...field, ariaLabel: field.placeholder }}
                  onSelectChange={selectField}
                />
              ))}
            </div>
          </PracticeField>

          {ADDRESS_AREA_FIELDS.map((field) => (
            <ConfiguredField
              key={field.name}
              formik={formik}
              field={field}
              onSelectChange={selectField}
            />
          ))}

          <div className={styles.mapPanel}>
            <PracticeLocationMap
              mapsScript={mapsScript}
              eircode={formik.values.eircode}
              fullAddress={fullAddress}
              initialLatitude={formik.values.latitude}
              initialLongitude={formik.values.longitude}
              initialGeoHash={formik.values.geoHash}
              onEircodeChange={onEircodeChange}
              onLocationChange={onLocationChange}
            />
          </div>
        </div>
      )}

      <div className={styles.actions}>
        <Button variant="primary" size="large" type="submit" disabled={isSaving}>
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </div>
    </form>
  );
};
